import type { RetroSnapshot } from './types.js';

/**
 * Socket.IO contract.
 *
 * Clients only ever send *intents*; the server validates, persists, and then
 * pushes a fresh redacted snapshot. Snapshots rather than deltas keep the wire
 * format trivial to reason about and make reconnects free — at retro scale
 * (a handful of participants, a few hundred cards) the payload is tiny.
 */

export const ROTI_SCALE_LABELS: Record<number, { title: string; detail: string }> = {
  1: { title: 'Useless meeting', detail: "I feel like I completely wasted my time." },
  2: { title: 'No real value', detail: 'I could have used my time more effectively.' },
  3: { title: 'Fine retrospective', detail: 'My time was fairly well spent.' },
  4: { title: 'Good retrospective!', detail: "I'm happy with the time I invested." },
  5: { title: 'Great retrospective!', detail: 'Every minute I spent was valuable.' },
};

export type SocketErrorCode =
  | 'UNAUTHORISED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'INVALID_INPUT'
  | 'WRONG_PHASE'
  | 'LIMIT_REACHED'
  | 'CONFLICT'
  | 'INTERNAL';

export type SocketError = {
  code: SocketErrorCode;
  message: string;
};

/** Acknowledgement returned to the caller of every intent. */
export type Ack<T = undefined> =
  | ({ ok: true } & (T extends undefined ? { data?: undefined } : { data: T }))
  | { ok: false; error: SocketError };

export type ServerToClientEvents = {
  /** Full redacted state. Sent on join and after every mutation. */
  'retro:state': (snapshot: RetroSnapshot) => void;
  /** Transient, non-authoritative nudges (e.g. the timer ran out). */
  'retro:notice': (notice: { kind: 'TIMER_ELAPSED' | 'PHASE_CHANGED'; message: string }) => void;
  'retro:closed': (payload: { retroId: string }) => void;
};

export const RETRO_INTENTS = [
  'retro:sync',
  'retro:setReady',
  'retro:advancePhase',
  'retro:rename',
  'retro:updateSettings',
  'timer:start',
  'timer:pause',
  'timer:resume',
  'timer:stop',
  'comment:create',
  'comment:update',
  'comment:delete',
  'comment:move',
  'comment:group',
  'comment:ungroup',
  'group:rename',
  'group:toggleReaction',
  'comment:setTopics',
  'comment:toggleReaction',
  'topic:create',
  'vote:set',
  'suggestion:resolve',
  'suggestion:dropComment',
  'action:upsert',
  'action:delete',
  'action:setStatus',
  'roti:submit',
] as const;

export type RetroIntent = (typeof RETRO_INTENTS)[number];
